import {Box, Clickable, HStack, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {RecordAuditResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {
  RecordAuditScorableResult,
  computeRecordAuditScore,
} from '../../../score/computeRecordAuditScore';
import {RecordAuditProgressSegments} from './RecordAuditProgressSegments';

export type RecordAuditScoreFilter = 'all' | 'OK' | 'BAD' | 'NA' | 'notFilled';

interface RecordAuditScoreBarProps {
  audit: RecordAuditResponseBody;
  /**
   * When provided AND audit is OPEN, counts + score are recomputed live from
   * these (overlays applied). Otherwise BE-frozen `audit.statistics` is used.
   */
  liveResults?: ReadonlyArray<RecordAuditScorableResult>;
  activeFilter: RecordAuditScoreFilter;
  onFilterChange: (filter: RecordAuditScoreFilter) => void;
}

interface Counts {
  ok: number;
  bad: number;
  na: number;
  notFilled: number;
  total: number;
  okPoints: number;
  maxPoints: number;
  percentage: number | null;
  passed: boolean | null;
}

export function RecordAuditScoreBar(props: RecordAuditScoreBarProps) {
  const counts = resolveCounts(props.audit, props.liveResults);
  const filled = counts.total - counts.notFilled;
  const ratio = counts.total === 0 ? 0 : filled / counts.total;

  return (
    <VStack spacing={2}>
      <HStack justify="space-between" align="center" spacing={4} wrap>
        <HStack spacing={2} align="baseline">
          <Text size="xSmall" color="tertiary" alternative>
            {i18n.t('page.checklistAudits.detail.completion').toUpperCase()}
          </Text>
          <HStack spacing={1} align="baseline">
            <Text size="large">{`${filled}`}</Text>
            <Text size="large" color="secondary">{`/ ${counts.total}`}</Text>
          </HStack>
          <Text size="xSmall" color="tertiary">
            {`(${Math.round(ratio * 100)}%)`}
          </Text>
        </HStack>
        <HStack spacing={3} align="center" wrap>
          <ScoreLegendItem
            dotColor="palettes.green.60.100"
            label={i18n.t('page.checklistAudits.detail.scoreCounts.passed', {count: counts.ok})}
          />
          <ScoreLegendItem
            dotColor="palettes.red.60.100"
            label={i18n.t('page.checklistAudits.detail.scoreCounts.failed', {count: counts.bad})}
          />
          <ScoreLegendItem
            dotColor="palettes.neutral.70.100"
            label={i18n.t('page.checklistAudits.detail.scoreCounts.na', {count: counts.na})}
          />
          <ScoreLegendItem
            dotColor="palettes.neutral.40.100"
            label={i18n.t('page.checklistAudits.detail.scoreCounts.notFilled', {
              count: counts.notFilled,
            })}
          />
        </HStack>
      </HStack>
      <RecordAuditProgressSegments
        ok={counts.ok}
        bad={counts.bad}
        na={counts.na}
        total={counts.total}
      />
      <HStack spacing={2} wrap align="center">
        <ScoreFilterPill
          label={i18n.t('general.labels.all')}
          count={counts.total}
          isActive={props.activeFilter === 'all'}
          onClick={() => props.onFilterChange('all')}
        />
        <ScoreFilterPill
          label={i18n.t('page.checklistAudits.detail.value.OK')}
          count={counts.ok}
          dotColor="palettes.green.60.100"
          isActive={props.activeFilter === 'OK'}
          onClick={() => props.onFilterChange('OK')}
        />
        <ScoreFilterPill
          label={i18n.t('page.checklistAudits.detail.value.BAD')}
          count={counts.bad}
          dotColor="palettes.red.60.100"
          isActive={props.activeFilter === 'BAD'}
          onClick={() => props.onFilterChange('BAD')}
        />
        <ScoreFilterPill
          label={i18n.t('page.checklistAudits.detail.value.NA')}
          count={counts.na}
          dotColor="palettes.neutral.70.100"
          isActive={props.activeFilter === 'NA'}
          onClick={() => props.onFilterChange('NA')}
        />
        <ScoreFilterPill
          label={i18n.t('page.checklistAudits.detail.score.notFilled')}
          count={counts.notFilled}
          dotColor="palettes.neutral.40.100"
          isActive={props.activeFilter === 'notFilled'}
          onClick={() => props.onFilterChange('notFilled')}
        />
      </HStack>
    </VStack>
  );
}

// 0.5 rem on the 4 px step grid (Box width multiplies the design-system step).
const LEGEND_DOT_SIZE = 2;

interface ScoreLegendItemProps {
  label: string;
  dotColor: ThemeColorPath;
}

// Read-only summary count beside the completion header (passed / failed / N/A / empty).
function ScoreLegendItem(props: ScoreLegendItemProps) {
  return (
    <HStack spacing={1} align="center">
      <Box
        width={LEGEND_DOT_SIZE}
        height={LEGEND_DOT_SIZE}
        borderRadius="circular"
        backgroundColor={props.dotColor}
      />
      <Text size="xSmall" color="tertiary" noWrap>
        {props.label}
      </Text>
    </HStack>
  );
}

const PILL_DOT_SIZE = '0.375rem';

interface ScoreFilterPillProps {
  label: string;
  count: number;
  dotColor?: ThemeColorPath;
  isActive?: boolean;
  onClick: () => void;
}

// Clickable filter pill matching the mockup: optional colour dot, label, and a
// count badge. The active filter renders as the dark pill.
function ScoreFilterPill(props: ScoreFilterPillProps) {
  return (
    <Clickable onClick={props.onClick}>
      <Box
        borderRadius="circular"
        border="1px solid"
        borderColor={props.isActive ? 'palettes.neutral.900.100' : 'palettes.neutral.40.100'}
        backgroundColor={props.isActive ? 'palettes.neutral.900.100' : 'general.white'}
        paddingHorizontal={2}
        paddingVertical={1}
      >
        <HStack spacing={1} align="center">
          <Show when={isNotNil(props.dotColor)}>
            <Box
              width={PILL_DOT_SIZE}
              height={PILL_DOT_SIZE}
              borderRadius="circular"
              backgroundColor={props.dotColor}
            />
          </Show>
          <Text size="xSmall" alternative color={props.isActive ? 'white' : 'primary'} noWrap>
            {props.label}
          </Text>
          <Box
            borderRadius="circular"
            backgroundColor={
              props.isActive ? 'palettes.neutral.700.100' : 'palettes.neutral.20.100'
            }
            paddingHorizontal={2}
          >
            <Text size="xSmall" color={props.isActive ? 'white' : 'tertiary'}>
              {props.count}
            </Text>
          </Box>
        </HStack>
      </Box>
    </Clickable>
  );
}

const resolveCounts = (
  audit: RecordAuditResponseBody,
  liveResults: ReadonlyArray<RecordAuditScorableResult> | undefined
): Counts => {
  if (audit.state === 'OPEN' && liveResults) {
    const ok = liveResults.filter((r) => r.value === 'OK').length;
    const bad = liveResults.filter((r) => r.value === 'BAD').length;
    const na = liveResults.filter((r) => r.value === 'NA').length;
    const score = computeRecordAuditScore(liveResults, audit.definitionSnapshot.successThreshold);
    return {
      ok,
      bad,
      na,
      notFilled: liveResults.length - ok - bad - na,
      total: liveResults.length,
      okPoints: score.okPoints,
      maxPoints: score.maxPoints,
      percentage: score.percentage,
      passed: score.passed,
    };
  }

  const {okCount, badCount, naCount, totalCount, score} = audit.statistics;
  return {
    ok: okCount,
    bad: badCount,
    na: naCount,
    notFilled: totalCount - okCount - badCount - naCount,
    total: totalCount,
    okPoints: score.okPoints,
    maxPoints: score.maxPoints,
    percentage: score.percentage,
    passed: score.passed,
  };
};
